import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAuth } from '../contexts/AuthContext';
import GTMService, { GTMEventName } from '../gtm';
import PostHogService, { OnboardingEvent } from '../posthog';
import toast from '../design-system/toast';
import { useOrganization } from '../contexts/OrganizationContext';
import { Spinner } from '../design-system';

const OrganizationSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user: clerkUser } = useUser();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();

  const [error, setError] = useState<string | null>(null);
  const hasAttemptedCreation = useRef(false);
  const signupMethod = clerkUser?.externalAccounts?.length
    ? `${clerkUser.externalAccounts[0].provider}_oauth`
    : 'email';

  useEffect(() => {
    if (hasAttemptedCreation.current) return;
    if (orgLoading) return;
    if (currentOrganization?.id) {
      hasAttemptedCreation.current = true;
      navigate(`/repositories?org=${currentOrganization.id}`, { replace: true });
      return;
    }
    // Wait for Clerk to finish loading the user so we have a stable ID to key
    // the signup-event guard by. Without this, the effect can fire with
    // user_id: undefined on first render.
    if (!clerkUser?.id) return;
    hasAttemptedCreation.current = true;

    const setupOrganization = async () => {
      try {
        // Key the fired-flag by Clerk user ID so user B's signup in the same
        // tab as user A fires its own events. Previously the shared key
        // `gtm_registration_complete_fired` was never cleared on logout and
        // blocked subsequent signups in the same tab session.
        const signupFiredKey = `signup_events_fired:${clerkUser.id}`;
        if (!sessionStorage.getItem(signupFiredKey)) {
          GTMService.push(GTMEventName.REGISTRATION_COMPLETE, {
            signup_method: signupMethod,
            user_id: clerkUser.id,
          });
          PostHogService.track('user_signed_up', {
            signup_method: signupMethod,
            user_id: clerkUser.id,
          });
          sessionStorage.setItem(signupFiredKey, '1');
        }

        navigate('/onboarding/connect', { replace: true });
      } catch (err: any) {
        console.error('Failed to create organization:', err);
        const errorMessage = err.data?.detail || err.message || 'Failed to create organization. Please try again.';
        PostHogService.track(OnboardingEvent.ORG_CREATE_FAILED, {
          reason: errorMessage,
        });
        setError(errorMessage);
        toast.error(errorMessage);
      }
    };

    setupOrganization();
  }, [clerkUser?.id, currentOrganization?.id, navigate, orgLoading, signupMethod]);

  // Only show UI if there's an error, otherwise render minimal loading
  if (error) {
    return (
      <div className="min-h-screen bg-ui-background-primary text-ui-text-primary flex flex-col">
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">Setup Failed</h1>
              <p className="text-ui-text-secondary">
                We couldn't set up your account.
              </p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 px-4 bg-brand-primary hover:bg-brand-primaryDark
                         text-black font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>

            <div className="mt-8 text-center">
              <p className="text-sm text-ui-text-tertiary">
                Wrong account?{' '}
                <button
                  onClick={() => logout()}
                  className="text-brand-primary dark:text-brand-primaryDarkMode hover:underline"
                >
                  Sign out
                </button>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Minimal loading state while redirecting
  return (
    <div className="min-h-screen bg-ui-background-primary flex items-center justify-center">
      <Spinner size={32} />
    </div>
  );
};

export default OrganizationSetupPage;
